import React from 'react';
import { Box, Typography, Paper, Tooltip, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Emoji, EmojiData } from '@appTypes/emoji';
import { EmojiImage } from '@components/shared';
import { isFlagEmoji } from '@utils/twemojiUtils';

interface PopularEmojisProps {
  chars: string[];
  data: EmojiData;
  onEmojiClick: (emoji: string, event: React.SyntheticEvent<HTMLElement>) => void;
  onClear: () => void;
}

export const PopularEmojis = React.memo(
  ({ chars, data, onEmojiClick, onClear }: PopularEmojisProps) => {
    const { t } = useTranslation();

    // Build a char → Emoji lookup from the emoji data, so a copied char can recover
    // both its display name and whether it needs the image fallback (new code points
    // too recent for most OS fonts, same as the grid's rendering rule).
    const emojiMap = React.useMemo(() => {
      const map = new Map<string, Emoji>();
      for (const category of data.categories) {
        if (category.subgroups) {
          for (const subgroup of category.subgroups) {
            for (const emoji of subgroup.emojis) {
              map.set(emoji.char, emoji);
            }
          }
        } else if (category.emojis) {
          for (const emoji of category.emojis) {
            map.set(emoji.char, emoji);
          }
        }
      }
      return map;
    }, [data]);

    if (chars.length === 0) return null;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
          {t('home.popularEmojis')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {chars.map((char) => {
            const entry = emojiMap.get(char);
            const name = entry ? t(entry.nameKey, char) : char;
            // Flags never render as native glyphs on Windows; renderAsImage covers
            // code points too new for the OS font regardless of platform.
            const renderAsImage = isFlagEmoji(char) || entry?.renderAsImage === true;
            return (
              <Tooltip key={char} title={name} arrow>
                <Paper
                  elevation={1}
                  role="button"
                  tabIndex={0}
                  aria-label={t('a11y.copyEmoji', { name })}
                  onClick={(e) => onEmojiClick(char, e)}
                  onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      // Space scrolls the page by default — suppress that.
                      if (event.key === ' ') event.preventDefault();
                      onEmojiClick(char, event);
                    }
                  }}
                  sx={{
                    padding: { xs: 1, sm: 1.5 },
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minWidth: 40,
                    '&:hover': {
                      transform: 'scale(1.1)',
                      backgroundColor: '#f5f5f5',
                    },
                    '&:focus-visible': {
                      transform: 'scale(1.1)',
                      backgroundColor: '#f5f5f5',
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                    '@media (hover: none)': {
                      '&:hover': { transform: 'none' },
                      '&:active': { transform: 'scale(0.95)', backgroundColor: '#f0f0f0' },
                    },
                  }}
                >
                  {renderAsImage ? (
                    <EmojiImage emoji={char} fontSize="1.5rem" />
                  ) : (
                    <span style={{ fontSize: '1.5rem' }}>{char}</span>
                  )}
                </Paper>
              </Tooltip>
            );
          })}
          <Tooltip title={t('home.clearPopular')} arrow>
            <IconButton
              size="small"
              onClick={onClear}
              sx={{
                alignSelf: 'center',
                color: 'text.disabled',
                '&:hover': { color: 'text.secondary' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  }
);
PopularEmojis.displayName = 'PopularEmojis';
